import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { GoogleGenAI } from '@google/genai';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const selectStyles = {
	control: (base, state) => ({
		...base,
		borderRadius: '0.75rem',
		borderColor: state.isFocused ? '#6366F1' : '#D1D5DB',
		boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.15)' : 'none',
		paddingLeft: '4px',
		paddingRight: '4px',
		minHeight: '48px',
		'&:hover': {
			borderColor: state.isFocused ? '#4F46E5' : '#A5B4FC',
		},
	}),
	menu: (base) => ({
		...base,
		zIndex: 30,
	}),
	option: (base, state) => ({
		...base,
		backgroundColor: state.isSelected ? '#4F46E5' : state.isFocused ? '#EEF2FF' : base.backgroundColor,
		color: state.isSelected ? '#FFFFFF' : base.color,
		fontWeight: state.isSelected ? 600 : 400,
	}),
	singleValue: (base) => ({
		...base,
		fontWeight: 500,
	}),
	placeholder: (base) => ({
		...base,
		color: '#9CA3AF',
	}),
};

const menuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;

const GEMINI_API_KEY = 'AIzaSyA9PXZfWcFaAzfyZ7OnM6njIuvSOLdgHaA';
const genAI = GEMINI_API_KEY ? new GoogleGenAI({ apiKey: GEMINI_API_KEY }) : null;

const formatDate = (value) => {
	if (!value) {
		return null;
	}
	const normalized = value.includes('T') ? value : value.replace(' ', 'T');
	const parsed = new Date(normalized);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}
	return parsed.toLocaleDateString();
};

export default function Correction() {
	const [assignmentSubjects, setAssignmentSubjects] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const [selectedSubjectId, setSelectedSubjectId] = useState(null);
	const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
	const [selectedCriteriaId, setSelectedCriteriaId] = useState(null);

	const [criteriaSearch, setCriteriaSearch] = useState('');
	const [gradingSearch, setGradingSearch] = useState('');
	const [gradingStatusFilter, setGradingStatusFilter] = useState('all'); // 'all' | 'achieved' | 'not-achieved'

	const [subjectModalSearch, setSubjectModalSearch] = useState('');
	const [assignmentModalSearch, setAssignmentModalSearch] = useState('');
	const [criteriaModalSearch, setCriteriaModalSearch] = useState('');
	const [gradingModalSearch, setGradingModalSearch] = useState('');

	const [selectedGradingIds, setSelectedGradingIds] = useState(new Set());
	const [gradingStatusOverrides, setGradingStatusOverrides] = useState(new Map());

	const [showSubjectModal, setShowSubjectModal] = useState(false);
	const [subjectModalMode, setSubjectModalMode] = useState('create');
	const [editingSubjectId, setEditingSubjectId] = useState(null);
	const [subjectForm, setSubjectForm] = useState({ name: '' });
	const [subjectErrors, setSubjectErrors] = useState({});
	const [subjectSubmitting, setSubjectSubmitting] = useState(false);

	const [showAssignmentModal, setShowAssignmentModal] = useState(false);
	const [assignmentModalMode, setAssignmentModalMode] = useState('create');
	const [editingAssignmentId, setEditingAssignmentId] = useState(null);
	const [assignmentForm, setAssignmentForm] = useState({ title: '', subject_id: null });
	const [assignmentErrors, setAssignmentErrors] = useState({});
	const [assignmentSubmitting, setAssignmentSubmitting] = useState(false);

	const [showCriteriaModal, setShowCriteriaModal] = useState(false);
	const [criteriaModalMode, setCriteriaModalMode] = useState('create');
	const [editingCriteriaId, setEditingCriteriaId] = useState(null);
	const [criteriaForm, setCriteriaForm] = useState({ assignment_id: null, name: '' });
	const [criteriaErrors, setCriteriaErrors] = useState({});
	const [criteriaSubmitting, setCriteriaSubmitting] = useState(false);

	const [showGradingModal, setShowGradingModal] = useState(false);
	const [gradingModalMode, setGradingModalMode] = useState('create');
	const [editingGradingId, setEditingGradingId] = useState(null);
	const [gradingForm, setGradingForm] = useState({ criteria_id: null, name: '', status: 'Achieved' });
	const [gradingErrors, setGradingErrors] = useState({});
	const [gradingSubmitting, setGradingSubmitting] = useState(false);

	const [showPromptPreviewModal, setShowPromptPreviewModal] = useState(false);
	const [promptPreviewText, setPromptPreviewText] = useState('');
	const [feedbackWordCount, setFeedbackWordCount] = useState(20);
	const [geminiResponseText, setGeminiResponseText] = useState('');
	const [geminiLoading, setGeminiLoading] = useState(false);
	const [geminiError, setGeminiError] = useState('');

	const loadAssignmentSubjects = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const resp = await axiosInstance.get('/admin/assignment-corrections');
			const data = resp?.data?.assignmentSubjects || [];
			setAssignmentSubjects(data);
		} catch (err) {
			setError(err?.response?.data?.message || 'Failed to load correction data');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadAssignmentSubjects();
	}, [loadAssignmentSubjects]);

	useEffect(() => {
		if (assignmentSubjects.length === 0) {
			setSelectedSubjectId(null);
			setSelectedAssignmentId(null);
			setSelectedCriteriaId(null);
			return;
		}

		if (!assignmentSubjects.some((subject) => subject.id === selectedSubjectId)) {
			setSelectedSubjectId(assignmentSubjects[0]?.id ?? null);
			setSelectedAssignmentId(null);
			setSelectedCriteriaId(null);
		}
	}, [assignmentSubjects, selectedSubjectId]);

	useEffect(() => {
		setSelectedAssignmentId(null);
		setSelectedCriteriaId(null);
	}, [selectedSubjectId]);

	useEffect(() => {
		setCriteriaSearch('');
		setGradingSearch('');
		setSelectedCriteriaId(null);
	}, [selectedAssignmentId]);

	const selectedSubject = useMemo(() => {
		if (selectedSubjectId === null) {
			return null;
		}
		return assignmentSubjects.find((subject) => subject.id === selectedSubjectId) ?? null;
	}, [assignmentSubjects, selectedSubjectId]);

	const subjectOptions = useMemo(() => {
		return assignmentSubjects.map((subject) => ({
			value: subject.id,
			label: subject.name,
		}));
	}, [assignmentSubjects]);

	const filteredSubjectsForModal = useMemo(() => {
		const term = subjectModalSearch.trim().toLowerCase();
		if (term === '') {
			return assignmentSubjects;
		}
		return assignmentSubjects.filter((subject) => subject.name.toLowerCase().includes(term));
	}, [assignmentSubjects, subjectModalSearch]);

	const assignmentOptions = useMemo(() => {
		if (!selectedSubject) {
			return [];
		}
		return (selectedSubject.assignments || []).map((assignment) => ({
			value: assignment.id,
			label: assignment.title,
		}));
	}, [selectedSubject]);

	const allAssignments = useMemo(() => {
		return assignmentSubjects.flatMap((subject) =>
			(subject.assignments || []).map((assignment) => ({ assignment, subject }))
		);
	}, [assignmentSubjects]);

	const assignmentsForModal = useMemo(() => {
		if (!assignmentForm.subject_id) {
			return allAssignments;
		}
		return allAssignments.filter((row) => row.subject.id === assignmentForm.subject_id);
	}, [allAssignments, assignmentForm.subject_id]);

	const filteredAssignmentsForModal = useMemo(() => {
		const term = assignmentModalSearch.trim().toLowerCase();
		if (term === '') {
			return assignmentsForModal;
		}
		return assignmentsForModal.filter(({ assignment, subject }) => {
			return assignment.title.toLowerCase().includes(term) || subject.name.toLowerCase().includes(term);
		});
	}, [assignmentModalSearch, assignmentsForModal]);

	const assignmentOptionsForCriteriaModal = useMemo(() => {
		return allAssignments.map(({ assignment, subject }) => ({
			value: assignment.id,
			label: `${subject.name} · ${assignment.title}`,
		}));
	}, [allAssignments]);

	const allCriteriaRows = useMemo(() => {
		return allAssignments.flatMap(({ assignment, subject }) =>
			(assignment.criteria || []).map((criteria) => ({ criteria, assignment, subject }))
		);
	}, [allAssignments]);

	const criteriaLookup = useMemo(() => {
		const map = new Map();
		allCriteriaRows.forEach((row) => {
			map.set(row.criteria.id, row);
		});
		return map;
	}, [allCriteriaRows]);

	const criteriaOptionsForGradingModal = useMemo(() => {
		return allCriteriaRows.map(({ criteria, assignment, subject }) => ({
			value: criteria.id,
			label: `${subject.name} · ${assignment.title} · ${criteria.name}`,
		}));
	}, [allCriteriaRows]);

	const gradingRows = useMemo(() => {
		return allCriteriaRows.flatMap((criteriaRow) =>
			(criteriaRow.criteria.gradings || []).map((grading) => ({
				grading,
				criteria: criteriaRow.criteria,
				assignment: criteriaRow.assignment,
				subject: criteriaRow.subject,
			}))
		);
	}, [allCriteriaRows]);

	const gradingLookup = useMemo(() => {
		const map = new Map();
		gradingRows.forEach((row) => {
			map.set(row.grading.id, row);
		});
		return map;
	}, [gradingRows]);

	const criteriaRowsForModal = useMemo(() => {
		if (criteriaForm.assignment_id) {
			return allCriteriaRows.filter((row) => row.assignment.id === criteriaForm.assignment_id);
		}
		return allCriteriaRows;
	}, [allCriteriaRows, criteriaForm.assignment_id]);

	const filteredCriteriaRowsForModal = useMemo(() => {
		const term = criteriaModalSearch.trim().toLowerCase();
		if (term === '') {
			return criteriaRowsForModal;
		}
		return criteriaRowsForModal.filter(({ criteria, assignment, subject }) => {
			return (
				criteria.name.toLowerCase().includes(term) ||
				assignment.title.toLowerCase().includes(term) ||
				subject.name.toLowerCase().includes(term)
			);
		});
	}, [criteriaModalSearch, criteriaRowsForModal]);

	const selectedAssignmentRow = useMemo(() => {
		if (!selectedAssignmentId) {
			return null;
		}
		return allAssignments.find((row) => row.assignment.id === selectedAssignmentId) ?? null;
	}, [allAssignments, selectedAssignmentId]);

	const selectedAssignment = selectedAssignmentRow?.assignment ?? null;

	useEffect(() => {
		if (!selectedAssignment || (selectedAssignment.criteria || []).length === 0) {
			if (selectedCriteriaId !== null) {
				setSelectedCriteriaId(null);
			}
			return;
		}

		const exists = (selectedAssignment.criteria || []).some((criteria) => criteria.id === selectedCriteriaId);
		if (!exists) {
			setSelectedCriteriaId(selectedAssignment.criteria[0].id);
		}
	}, [selectedAssignment, selectedCriteriaId]);

	useEffect(() => {
		if (!showCriteriaModal || criteriaModalMode !== 'create') {
			return;
		}
		setCriteriaForm((prev) => {
			const preferredAssignmentId = selectedAssignment?.id ?? null;
			if (prev.assignment_id === preferredAssignmentId) {
				return prev;
			}
			return { ...prev, assignment_id: preferredAssignmentId };
		});
	}, [showCriteriaModal, criteriaModalMode, selectedAssignment]);

	const criteriaList = selectedAssignment?.criteria || [];

	const filteredCriteria = useMemo(() => {
		const term = criteriaSearch.trim().toLowerCase();
		if (term === '') {
			return criteriaList;
		}
		return criteriaList.filter((criteria) => criteria.name.toLowerCase().includes(term));
	}, [criteriaList, criteriaSearch]);

	const selectedCriteria = useMemo(() => {
		if (!selectedAssignment) {
			return null;
		}
		return (selectedAssignment.criteria || []).find((criteria) => criteria.id === selectedCriteriaId) ?? null;
	}, [selectedAssignment, selectedCriteriaId]);

	useEffect(() => {
		if (!showGradingModal || gradingModalMode !== 'create') {
			return;
		}
		setGradingForm((prev) => {
			const preferredCriteriaId = selectedCriteria?.id ?? null;
			if (prev.criteria_id === preferredCriteriaId) {
				return prev;
			}
			return { ...prev, criteria_id: preferredCriteriaId };
		});
	}, [showGradingModal, gradingModalMode, selectedCriteria]);

	const gradingRowsForModal = useMemo(() => {
		if (gradingForm.criteria_id) {
			return gradingRows.filter((row) => row.criteria.id === gradingForm.criteria_id);
		}
		if (selectedCriteria) {
			return gradingRows.filter((row) => row.criteria.id === selectedCriteria.id);
		}
		return gradingRows;
	}, [gradingRows, gradingForm.criteria_id, selectedCriteria]);

	const filteredGradingRowsForModal = useMemo(() => {
		const term = gradingModalSearch.trim().toLowerCase();
		if (term === '') {
			return gradingRowsForModal;
		}
		return gradingRowsForModal.filter(({ grading, criteria, assignment, subject }) => {
			return (
				grading.name.toLowerCase().includes(term) ||
				(grading.status ?? '').toLowerCase().includes(term) ||
				criteria.name.toLowerCase().includes(term) ||
				assignment.title.toLowerCase().includes(term) ||
				subject.name.toLowerCase().includes(term)
			);
		});
	}, [gradingModalSearch, gradingRowsForModal]);

	function resolveGradingStatus(grading) {
		return gradingStatusOverrides.get(grading.id) ?? grading.status ?? null;
	}

	const filteredGradings = useMemo(() => {
		const base = selectedCriteria?.gradings || [];
		let result = base;

		if (gradingStatusFilter !== 'all') {
			result = result.filter((grading) => {
				const status = resolveGradingStatus(grading);
				if (gradingStatusFilter === 'achieved') {
					return status === 'Achieved';
				}
				if (gradingStatusFilter === 'not-achieved') {
					return status === 'Not Achieved';
				}
				return true;
			});
		}

		const term = gradingSearch.trim().toLowerCase();
		if (term === '') {
			return result;
		}
		// Split search terms by spaces and filter out empty strings
		const searchWords = term.split(/\s+/).filter(word => word.length > 0);
		return result.filter((grading) => {
			const gradingText = grading.name.toLowerCase();
			// Check if all search words appear anywhere in the grading text
			return searchWords.every(word => gradingText.includes(word));
		});
	}, [selectedCriteria, gradingSearch, gradingStatusFilter, gradingStatusOverrides]);

	const hasSelectedGradings = selectedGradingIds.size > 0;

	useEffect(() => {
		setSelectedGradingIds(new Set());
	}, [selectedCriteriaId]);

	const toggleSelectedGrading = (gradingId) => {
		setSelectedGradingIds((prev) => {
			const next = new Set(prev);
			if (next.has(gradingId)) {
				next.delete(gradingId);
			} else {
				next.add(gradingId);
			}
			return next;
		});
	};

	const setGradingStatus = (gradingId, status) => {
		setGradingStatusOverrides((prev) => {
			const next = new Map(prev);
			next.set(gradingId, status);
			return next;
		});
	};

	useEffect(() => {
		if (assignmentOptions.length === 0) {
			setSelectedAssignmentId(null);
			return;
		}
		if (selectedAssignmentId !== null && !assignmentOptions.some((option) => option.value === selectedAssignmentId)) {
			setSelectedAssignmentId(null);
		}
	}, [assignmentOptions, selectedAssignmentId]);

	const handleSubjectChange = (option) => {
		setSelectedSubjectId(option ? option.value : null);
	};

	const handleAssignmentChange = (option) => {
		setSelectedAssignmentId(option ? option.value : null);
	};

	useEffect(() => {
		if (!showAssignmentModal || assignmentModalMode !== 'create') {
			return;
		}
		if (!selectedSubject) {
			setAssignmentForm((prev) => {
				if (prev.subject_id === null) {
					return prev;
				}
				return { ...prev, subject_id: null };
			});
			return;
		}
		setAssignmentForm((prev) => {
			if (prev.subject_id === selectedSubject.id) {
				return prev;
			}
			return { ...prev, subject_id: selectedSubject.id };
		});
	}, [selectedSubject, showAssignmentModal, assignmentModalMode]);

	const openSubjectModal = () => {
		setSubjectModalMode('create');
		setEditingSubjectId(null);
		setSubjectForm({ name: '' });
		setSubjectErrors({});
		setShowSubjectModal(true);
	};

	const editSubject = (subject) => {
		setSubjectModalMode('edit');
		setEditingSubjectId(subject.id);
		setSubjectForm({ name: subject.name });
		setSubjectErrors({});
		setShowSubjectModal(true);
	};

	const closeSubjectModal = () => {
		setShowSubjectModal(false);
		setSubjectModalMode('create');
		setEditingSubjectId(null);
		setSubjectForm({ name: '' });
		setSubjectErrors({});
		setSubjectSubmitting(false);
		setSubjectModalSearch('');
	};

	const submitSubjectForm = async (event) => {
		event.preventDefault();
		const name = subjectForm.name.trim();
		if (!name) {
			setSubjectErrors({ name: 'Subject name is required' });
			return;
		}
		setSubjectSubmitting(true);
		try {
			if (subjectModalMode === 'edit' && editingSubjectId) {
				await axiosInstance.put(`/admin/assignment-corrections/subjects/${editingSubjectId}`, { name });
				Swal.fire('Updated', 'Assignment subject updated', 'success');
			} else {
				await axiosInstance.post('/admin/assignment-corrections/subjects', { name });
				Swal.fire('Created', 'Assignment subject created', 'success');
			}
			await loadAssignmentSubjects();
			closeSubjectModal();
		} catch (err) {
			const message = err?.response?.data?.message || 'Unable to save subject';
			setSubjectErrors({ form: message });
			Swal.fire('Error', message, 'error');
		} finally {
			setSubjectSubmitting(false);
		}
	};

	const deleteSubject = (subject) => {
		Swal.fire({
			icon: 'warning',
			title: 'Delete assignment subject?',
			text:
				subject.assignment_count > 0
					? `This will remove ${subject.assignment_count} assignment${subject.assignment_count === 1 ? '' : 's'} linked to this subject.`
					: 'This subject will be removed.',
			showCancelButton: true,
			confirmButtonText: 'Delete',
			confirmButtonColor: '#DC2626',
		}).then(async (result) => {
			if (!result.isConfirmed) {
				return;
			}
			try {
				await axiosInstance.delete(`/admin/assignment-corrections/subjects/${subject.id}`);
				await loadAssignmentSubjects();
				Swal.fire('Deleted', 'Assignment subject removed', 'success');
			} catch (err) {
				Swal.fire('Error', err?.response?.data?.message || 'Failed to delete subject', 'error');
			}
		});
	};

	const openAssignmentModal = () => {
		if (!selectedSubject) {
			Swal.fire({
				icon: 'info',
				title: 'Select a subject first',
				text: 'Choose an assignment subject before managing assignments.',
				confirmButtonText: 'OK',
			});
			return;
		}
		setAssignmentModalMode('create');
		setEditingAssignmentId(null);
		setAssignmentForm({ title: '', subject_id: selectedSubject.id });
		setAssignmentErrors({});
		setShowAssignmentModal(true);
	};

	const editAssignment = (assignment) => {
		setAssignmentModalMode('edit');
		setEditingAssignmentId(assignment.id);
		setAssignmentForm({ title: assignment.title, subject_id: assignment.subject_id });
		setAssignmentErrors({});
		setShowAssignmentModal(true);
	};

	const closeAssignmentModal = () => {
		setShowAssignmentModal(false);
		setAssignmentModalMode('create');
		setEditingAssignmentId(null);
		setAssignmentForm({ title: '', subject_id: selectedSubject?.id ?? assignmentSubjects[0]?.id ?? null });
		setAssignmentErrors({});
		setAssignmentSubmitting(false);
		setAssignmentModalSearch('');
	};

	const submitAssignmentForm = async (event) => {
		event.preventDefault();
		if (!assignmentForm.subject_id) {
			setAssignmentErrors((prev) => ({ ...prev, subject_id: 'Subject is required' }));
			return;
		}
		const title = assignmentForm.title.trim();
		if (!title) {
			setAssignmentErrors((prev) => ({ ...prev, title: 'Assignment title is required' }));
			return;
		}
		setAssignmentSubmitting(true);
		try {
			const payload = { title, subject_id: assignmentForm.subject_id };
			if (assignmentModalMode === 'edit' && editingAssignmentId) {
				await axiosInstance.put(`/admin/assignment-corrections/assignments/${editingAssignmentId}`, payload);
				Swal.fire('Updated', 'Assignment updated', 'success');
			} else {
				await axiosInstance.post('/admin/assignment-corrections/assignments', payload);
				Swal.fire('Created', 'Assignment created', 'success');
			}
			await loadAssignmentSubjects();
			closeAssignmentModal();
		} catch (err) {
			const message = err?.response?.data?.message || 'Unable to save assignment';
			setAssignmentErrors({ form: message });
			Swal.fire('Error', message, 'error');
		} finally {
			setAssignmentSubmitting(false);
		}
	};

	const deleteAssignment = (assignment) => {
		Swal.fire({
			icon: 'warning',
			title: 'Delete assignment?',
			text: `"${assignment.title}" will be removed.`,
			showCancelButton: true,
			confirmButtonText: 'Delete',
			confirmButtonColor: '#DC2626',
		}).then(async (result) => {
			if (!result.isConfirmed) {
				return;
			}
			try {
				await axiosInstance.delete(`/admin/assignment-corrections/assignments/${assignment.id}`);
				if (selectedAssignmentId === assignment.id) {
					setSelectedAssignmentId(null);
				}
				await loadAssignmentSubjects();
				Swal.fire('Deleted', 'Assignment removed', 'success');
			} catch (err) {
				Swal.fire('Error', err?.response?.data?.message || 'Failed to delete assignment', 'error');
			}
		});
	};

	const openCriteriaModal = () => {
		setCriteriaModalMode('create');
		setEditingCriteriaId(null);
		setCriteriaForm({ assignment_id: selectedAssignment?.id ?? criteriaForm.assignment_id ?? null, name: '' });
		setCriteriaErrors({});
		setShowCriteriaModal(true);
	};

	const editCriteria = (criteriaId) => {
		const criteriaRow = criteriaLookup.get(criteriaId);
		if (!criteriaRow) {
			return;
		}
		setCriteriaModalMode('edit');
		setEditingCriteriaId(criteriaId);
		setCriteriaForm({ assignment_id: criteriaRow.assignment.id, name: criteriaRow.criteria.name });
		setCriteriaErrors({});
		setShowCriteriaModal(true);
	};

	const closeCriteriaModal = () => {
		setShowCriteriaModal(false);
		setCriteriaModalMode('create');
		setEditingCriteriaId(null);
		setCriteriaForm({ assignment_id: selectedAssignment?.id ?? null, name: '' });
		setCriteriaErrors({});
		setCriteriaSubmitting(false);
		setCriteriaModalSearch('');
	};

	const submitCriteriaForm = async (event) => {
		event.preventDefault();
		const assignmentId = criteriaForm.assignment_id;
		if (!assignmentId) {
			setCriteriaErrors((prev) => ({ ...prev, assignment_id: 'Select an assignment first.' }));
			return;
		}
		const name = criteriaForm.name.trim();
		if (!name) {
			setCriteriaErrors((prev) => ({ ...prev, name: 'Criteria name is required' }));
			return;
		}
		setCriteriaSubmitting(true);
		try {
			const payload = { name, assignment_id: assignmentId };
			if (criteriaModalMode === 'edit' && editingCriteriaId) {
				await axiosInstance.put(`/admin/assignment-corrections/criteria/${editingCriteriaId}`, payload);
				Swal.fire('Updated', 'Criteria updated', 'success');
			} else {
				await axiosInstance.post(`/admin/assignment-corrections/assignments/${assignmentId}/criteria`, payload);
				Swal.fire('Created', 'Criteria created', 'success');
			}
			await loadAssignmentSubjects();
			closeCriteriaModal();
		} catch (err) {
			const message = err?.response?.data?.message || 'Unable to save criteria';
			setCriteriaErrors({ form: message });
			Swal.fire('Error', message, 'error');
		} finally {
			setCriteriaSubmitting(false);
		}
	};

	const deleteCriteria = (criteriaId) => {
		const criteriaRow = criteriaLookup.get(criteriaId);
		if (!criteriaRow) {
			return;
		}
		Swal.fire({
			icon: 'warning',
			title: 'Delete criteria?',
			text: 'All grading templates linked to this criteria will also be removed.',
			showCancelButton: true,
			confirmButtonColor: '#dc2626',
			confirmButtonText: 'Delete',
		}).then(async (result) => {
			if (!result.isConfirmed) {
				return;
			}
			try {
				await axiosInstance.delete(`/admin/assignment-corrections/criteria/${criteriaId}`);
				await loadAssignmentSubjects();
				Swal.fire('Deleted', 'Criteria removed', 'success');
			} catch (err) {
				Swal.fire('Error', err?.response?.data?.message || 'Failed to delete criteria', 'error');
			}
		});
	};

	const openGradingModal = () => {
		setGradingModalMode('create');
		setEditingGradingId(null);
		setGradingForm({ criteria_id: selectedCriteria?.id ?? gradingForm.criteria_id ?? selectedCriteriaId ?? null, name: '', status: 'Achieved' });
		setGradingErrors({});
		setShowGradingModal(true);
	};

	const editGrading = (gradingId) => {
		const gradingRow = gradingLookup.get(gradingId);
		if (!gradingRow) {
			return;
		}
		setGradingModalMode('edit');
		setEditingGradingId(gradingId);
		setGradingForm({ criteria_id: gradingRow.criteria.id, name: gradingRow.grading.name, status: gradingRow.grading.status ?? null });
		setGradingErrors({});
		setShowGradingModal(true);
	};

	const closeGradingModal = () => {
		setShowGradingModal(false);
		setGradingModalMode('create');
		setEditingGradingId(null);
		setGradingForm({ criteria_id: selectedCriteria?.id ?? null, name: '', status: 'Achieved' });
		setGradingErrors({});
		setGradingSubmitting(false);
		setGradingModalSearch('');
	};

	const submitGradingForm = async (event) => {
		event.preventDefault();
		const criteriaId = gradingForm.criteria_id;
		if (!criteriaId) {
			setGradingErrors((prev) => ({ ...prev, criteria_id: 'Select a criteria first.' }));
			return;
		}
		const name = gradingForm.name.trim();
		if (!name) {
			setGradingErrors((prev) => ({ ...prev, name: 'Grading comment is required' }));
			return;
		}
		setGradingSubmitting(true);
		try {
			const payload = { name, status: gradingForm.status, criteria_id: criteriaId };
			if (gradingModalMode === 'edit' && editingGradingId) {
				await axiosInstance.put(`/admin/assignment-corrections/gradings/${editingGradingId}`, payload);
				Swal.fire('Updated', 'Grading template updated', 'success');
			} else {
				await axiosInstance.post(`/admin/assignment-corrections/criteria/${criteriaId}/gradings`, payload);
				Swal.fire('Created', 'Grading template created', 'success');
			}
			await loadAssignmentSubjects();
			closeGradingModal();
		} catch (err) {
			const message = err?.response?.data?.message || 'Unable to save grading template';
			setGradingErrors({ form: message });
			Swal.fire('Error', message, 'error');
		} finally {
			setGradingSubmitting(false);
		}
	};

	const deleteGrading = (gradingId) => {
		const gradingRow = gradingLookup.get(gradingId);
		if (!gradingRow) {
			return;
		}
		Swal.fire({
			icon: 'warning',
			title: 'Delete grading template?',
			text: 'This grading option will be permanently removed.',
			showCancelButton: true,
			confirmButtonColor: '#dc2626',
			confirmButtonText: 'Delete',
		}).then(async (result) => {
			if (!result.isConfirmed) {
				return;
			}
			try {
				await axiosInstance.delete(`/admin/assignment-corrections/gradings/${gradingId}`);
				await loadAssignmentSubjects();
				Swal.fire('Deleted', 'Grading template removed', 'success');
			} catch (err) {
				Swal.fire('Error', err?.response?.data?.message || 'Failed to delete grading template', 'error');
			}
		});
	};

	const buildChatGptPrompt = (overrideWordCount) => {
		if (!selectedCriteria) {
			return null;
		}
		const selectedGradings = (selectedCriteria.gradings || []).filter((grading) =>
			selectedGradingIds.has(grading.id)
		);
		if (selectedGradings.length === 0) {
			return null;
		}
		const pointsBlock = selectedGradings
			.map((grading) => {
				const statusLabel = resolveGradingStatus(grading) ?? 'Status not provided';
				return `- ${statusLabel}: ${grading.name}`;
			})
			.join('\n');
		const effectiveCount = overrideWordCount ?? feedbackWordCount;
		return [
			'Please combine the points mentioned below into a single, meaningful paragraph by correcting the grammar and presenting the content in a formal manner. The comment to the learner should clearly state the achieved criteria and, based on the criteria that have not been achieved, provide relevant areas for improvement. All of this should be presented as one cohesive paragraph, not as multiple paragraphs.',
			'',
			'points:',
			pointsBlock,
			'',
			`Note: The final feedback should be expanded or condensed so it is close to ${effectiveCount} words while still sounding natural. Do not provide unrelated or repetitive sentences just to reach the word count. If I have not mentioned 'Not achieved', do not suggest any improvements.`,
		].join('\n');
	};

	const copyTextToClipboard = async (text) => {
		try {
			if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
			} else if (typeof document !== 'undefined') {
				const textarea = document.createElement('textarea');
				textarea.value = text;
				textarea.setAttribute('readonly', 'true');
				textarea.style.position = 'absolute';
				textarea.style.left = '-9999px';
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand('copy');
				document.body.removeChild(textarea);
			}
			return true;
		} catch (err) {
			return false;
		}
	};

	const copyChatGptComment = async () => {
		const message = buildChatGptPrompt();
		if (!message) {
			Swal.fire({
				icon: 'info',
				title: 'Nothing to copy',
				text: 'Select at least one grading option first.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 2500,
			});
			return;
		}
		const ok = await copyTextToClipboard(message);
		if (ok) {
			Swal.fire({
				icon: 'success',
				title: 'Copied!',
				text: 'Prompt copied to clipboard. Paste it into ChatGPT to generate the learner comment.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 2500,
			});
		} else {
			Swal.fire({
				icon: 'error',
				title: 'Copy failed',
				text: 'Could not copy the ChatGPT comment prompt. Please try again.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 3000,
			});
		}
	};

	const openPromptPreviewModal = () => {
		const message = buildChatGptPrompt();
		if (!message) {
			Swal.fire({
				icon: 'info',
				title: 'Nothing to preview',
				text: 'Select at least one grading option first.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 2500,
			});
			return;
		}
		setPromptPreviewText(message);
		setGeminiResponseText('');
		setGeminiError('');
		setShowPromptPreviewModal(true);
	};

	const closePromptPreviewModal = () => {
		setShowPromptPreviewModal(false);
		setPromptPreviewText('');
		setGeminiResponseText('');
		setGeminiError('');
		setGeminiLoading(false);
	};

	const handleGenerateGeminiComment = async () => {
		if (!promptPreviewText.trim()) {
			Swal.fire({
				icon: 'info',
				title: 'Prompt is empty',
				text: 'Edit the prompt before sending it to Gemini.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 2500,
			});
			return;
		}
		if (!GEMINI_API_KEY || !genAI) {
			Swal.fire({
				icon: 'error',
				title: 'Gemini API key missing',
				text: 'Set GEMINI_API_KEY in Correction.jsx before generating comments.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 3000,
			});
			return;
		}
		setGeminiLoading(true);
		setGeminiError('');
		setGeminiResponseText('');
		try {
			const isServiceUnavailable = (err) => {
				const status = err?.status ?? err?.response?.status ?? err?.code;
				if (status === 503) return true;
				const message = String(err?.message || '').toLowerCase();
				return message.includes('503') || message.includes('unavailable');
			};

			let result;
			try {
				// First try the faster Flash model
				result = await genAI.models.generateContent({
					model: 'gemini-2.5-flash',
					contents: promptPreviewText,
				});
			} catch (innerError) {
				// If Flash is temporarily unavailable, fall back to Pro
				if (isServiceUnavailable(innerError)) {
					result = await genAI.models.generateContent({
						model: 'gemini-2.5-pro',
						contents: promptPreviewText,
					});
				} else {
					throw innerError;
				}
			}
			let text = '';
			if (typeof result?.response?.text === 'function') {
				text = result.response.text();
			} else if (Array.isArray(result?.candidates) && result.candidates.length > 0) {
				const parts = result.candidates[0]?.content?.parts || [];
				text = parts.map((part) => part.text ?? '').join('\n').trim();
			}
			// Post-process Gemini response so it's ready to paste as feedback
			if (text) {
				// If Gemini included a placeholder like these, drop everything up to and including it
				const placeholderMarkers = [
					'[Select and insert the appropriate starting sentence here]',
					'[Insert appropriate opening sentence based on marks]',
				];
				placeholderMarkers.forEach((marker) => {
					const idx = text.indexOf(marker);
					if (idx !== -1) {
						const after = text.substring(idx + marker.length);
						// Remove any leading comma or space right after the placeholder
						text = after.replace(/^\s*,?\s*/,'').trimStart();
					}
				});
				// If there's an "Instructions for use" section, drop it and anything after it
				const instructionsIndex = text.indexOf('Instructions for use:');
				if (instructionsIndex !== -1) {
					text = text.substring(0, instructionsIndex).trimEnd();
				}
				// Strip markdown-style asterisks used for bullets and bold/italic
				text = text
					// lines that are only "*" or similar separators
					.replace(/^\s*\*+\s*$/gm, '')
					// leading bullet "* "
					.replace(/^\s*\*\s+/gm, '')
					// bold and italic markers
					.replace(/\*\*(.*?)\*\*/g, '$1')
					.replace(/\*(.*?)\*/g, '$1')
					// avoid semicolon-style separators; prefer full stops
					.replace(/;\s*/g, '. ')
					.trim();
			}
			setGeminiResponseText(text || 'No response text received from Gemini.');
			Swal.fire({
				icon: 'success',
				title: 'Gemini comment generated',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 2000,
			});
		} catch (error) {
			setGeminiError('Failed to generate Gemini comment. Please try again.');
			Swal.fire({
				icon: 'error',
				title: 'Gemini error',
				text: error?.message || 'Failed to generate Gemini comment.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 3000,
			});
		} finally {
			setGeminiLoading(false);
		}
	};

	const copyGeminiResponseText = async () => {
		if (!geminiResponseText.trim()) {
			Swal.fire({
				icon: 'info',
				title: 'Nothing to copy',
				text: 'Generate a Gemini comment first.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 2500,
			});
			return;
		}
		const ok = await copyTextToClipboard(geminiResponseText);
		if (ok) {
			Swal.fire({
				icon: 'success',
				title: 'Copied!',
				text: 'Gemini comment copied to clipboard.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 2500,
			});
		} else {
			Swal.fire({
				icon: 'error',
				title: 'Copy failed',
				text: 'Could not copy the Gemini comment. Please try again.',
				toast: true,
				position: 'top-end',
				showConfirmButton: false,
				timer: 3000,
			});
		}
	};

	const subjectSelectValue = subjectOptions.find((option) => option.value === selectedSubjectId) ?? null;
	const assignmentSelectValue = assignmentOptions.find((option) => option.value === selectedAssignmentId) ?? null;

	return (
		<DashboardLayout title="Correction">
			<div className="space-y-6">
				{error && (
					<div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
						{error}
					</div>
				)}
				{loading && (
					<div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
						Loading correction data...
					</div>
				)}

				<div className="grid gap-6 md:grid-cols-2">
					<div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
						<div className="mb-3 flex items-center justify-between">
							<div>
								<p className="text-sm font-semibold text-gray-900">Assignment Subject</p>
								<p className="text-xs text-gray-500">Choose a subject to view its assignments</p>
							</div>
							<button
								type="button"
								onClick={openSubjectModal}
								className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm transition hover:bg-indigo-700"
								aria-label="Manage assignment subjects"
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
								</svg>
							</button>
						</div>
						<Select
							value={subjectSelectValue}
							onChange={handleSubjectChange}
							options={subjectOptions}
							isClearable
							placeholder="Select a subject"
							classNamePrefix="react-select"
							styles={selectStyles}
							menuPortalTarget={menuPortalTarget}
						/>
					</div>

					<div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
						<div className="mb-3 flex items-center justify-between">
							<div>
								<p className="text-sm font-semibold text-gray-900">Assignment</p>
								<p className="text-xs text-gray-500">Filtered by the selected subject</p>
							</div>
							<button
								type="button"
								onClick={openAssignmentModal}
								className={`flex h-10 w-10 items-center justify-center rounded-full text-white shadow-sm transition ${selectedSubject ? 'bg-emerald-600 hover:bg-emerald-700' : 'cursor-not-allowed bg-gray-300'}`}
								aria-label="Manage assignments"
								disabled={!selectedSubject}
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
								</svg>
							</button>
						</div>
						<Select
							value={assignmentSelectValue}
							onChange={handleAssignmentChange}
							options={assignmentOptions}
							isClearable
							placeholder={selectedSubject ? 'Select an assignment' : 'Choose a subject first'}
							classNamePrefix="react-select"
							styles={selectStyles}
							menuPortalTarget={menuPortalTarget}
							isDisabled={!selectedSubject || assignmentOptions.length === 0}
							noOptionsMessage={() => (selectedSubject ? 'No assignments found' : 'Select a subject first')}
						/>
					</div>
				</div>

				<div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
					<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-lg font-semibold text-gray-900">Criteria &amp; Grading</p>
							<p className="text-xs text-gray-500">Pick an assignment to review its criteria and grading templates.</p>
						</div>
						{(hasSelectedGradings || selectedAssignment) && (
							<div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
								{hasSelectedGradings && (
									<div className="flex flex-wrap items-center gap-3">
										<button
											type="button"
											onClick={copyChatGptComment}
											className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
										>
											Copy ChatGPT comment
										</button>
										<button
											type="button"
											onClick={openPromptPreviewModal}
											className="rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
										>
											Preview &amp; Gemini
										</button>
									</div>
								)}
								{selectedAssignment && (
									<div className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
										{selectedAssignmentRow?.subject.name ?? 'Subject'} · {selectedAssignment.title}
									</div>
								)}
							</div>
						)}
					</div>

					{selectedAssignment ? (
						<div className="grid gap-6 lg:grid-cols-2">
							<div>
								<label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Criteria</label>
								<div className="mb-4 flex items-center gap-3">
									<div className="relative flex-1">
										<input
											type="text"
											value={criteriaSearch}
											onChange={(event) => setCriteriaSearch(event.target.value)}
											className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
											placeholder="Search criteria"
										/>
										<svg
											className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
										</svg>
									</div>
									<button
										type="button"
										onClick={openCriteriaModal}
										disabled={assignmentOptionsForCriteriaModal.length === 0}
										className={`flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ${
											assignmentOptionsForCriteriaModal.length === 0
												? 'cursor-not-allowed bg-gray-200 text-gray-400'
												: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
										}`}
										aria-label="Manage criteria"
									>
										<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
										</svg>
									</button>
								</div>
								<div className="max-h-96 space-y-3 overflow-y-auto pr-2">
									{filteredCriteria.length > 0 ? (
										filteredCriteria.map((criteria) => {
											const isActive = criteria.id === selectedCriteriaId;
											return (
												<div
													key={criteria.id}
													onClick={() => setSelectedCriteriaId(criteria.id)}
													className={`w-full rounded-xl border px-4 py-3 cursor-pointer transition ${
														isActive
															? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
															: 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/40'
													}`}
												>
													<div className="flex items-start justify-between gap-2">
														<p className="text-sm font-semibold select-text flex-1">{criteria.name}</p>
														<button
															type="button"
															onClick={async (event) => {
																event.stopPropagation();
																const ok = await copyTextToClipboard(criteria.name);
																if (ok) {
																	Swal.fire({
																		icon: 'success',
																		title: 'Copied',
																		text: 'Criteria copied to clipboard.',
																		toast: true,
																		position: 'top-end',
																		showConfirmButton: false,
																		timer: 2000,
																	});
																} else {
																	Swal.fire({
																		icon: 'error',
																		title: 'Copy failed',
																		text: 'Could not copy the criteria.',
																		toast: true,
																		position: 'top-end',
																		showConfirmButton: false,
																		timer: 2000,
																	});
																}
															}}
															className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-100 flex-shrink-0"
														>
															Copy
														</button>
													</div>
													<p className="mt-2 text-xs text-gray-500 select-text">{(criteria.gradings || []).length} grading option{(criteria.gradings || []).length === 1 ? '' : 's'}</p>
												</div>
											);
										})
									) : (
										<div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
											{criteriaList.length > 0 ? 'No criteria match your search.' : 'No criteria defined for this assignment yet.'}
										</div>
									)}
								</div>
							</div>

							<div>
								<label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Grading</label>
								<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
									<div className="relative flex-1">
										<input
											type="text"
											value={gradingSearch}
											onChange={(event) => setGradingSearch(event.target.value)}
											className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
											placeholder="Search grading"
										/>
										<svg
											className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
										</svg>
									</div>
									<div className="flex flex-wrap items-center gap-1 text-xs">
										<span className="mr-1 text-[11px] font-medium text-gray-500">Status:</span>
										<button
											type="button"
											onClick={() => setGradingStatusFilter('all')}
											className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
												gradingStatusFilter === 'all'
													? 'bg-indigo-600 text-white shadow-sm'
													: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
											}`}
										>
											All
										</button>
										<button
											type="button"
											onClick={() => setGradingStatusFilter('achieved')}
											className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
												gradingStatusFilter === 'achieved'
													? 'bg-emerald-600 text-white shadow-sm'
													: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
											}`}
										>
											Achieved
										</button>
										<button
											type="button"
											onClick={() => setGradingStatusFilter('not-achieved')}
											className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
												gradingStatusFilter === 'not-achieved'
													? 'bg-rose-600 text-white shadow-sm'
													: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
											}`}
										>
											Not Achieved
										</button>
									</div>
								<button
										type="button"
										onClick={openGradingModal}
										disabled={criteriaOptionsForGradingModal.length === 0}
										className={`flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus:ring-2 focus:ring-indigo-500/30 ${
											criteriaOptionsForGradingModal.length === 0
												? 'cursor-not-allowed bg-gray-200 text-gray-400'
												: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
										}`}
										aria-label="Manage grading templates"
									>
										<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
										</svg>
									</button>
								</div>

								{selectedCriteria ? (
									<div className="max-h-96 space-y-3 overflow-y-auto pr-2">
										{filteredGradings.length > 0 ? (
											filteredGradings.map((grading) => {
												const effectiveStatus = resolveGradingStatus(grading);
												const badgeClasses = effectiveStatus === 'Achieved'
													? 'bg-emerald-100 text-emerald-700'
													: effectiveStatus === 'Not Achieved'
														? 'bg-rose-100 text-rose-700'
														: 'bg-gray-100 text-gray-600';
												const updatedDisplay = formatDate(grading.updatedAt);
												const isChecked = selectedGradingIds.has(grading.id);
												let cardClasses;
												if (effectiveStatus === 'Achieved') {
													cardClasses = isChecked
														? 'border-emerald-500 bg-emerald-50 shadow-md'
														: 'border-emerald-300 bg-emerald-50/60 hover:border-emerald-400';
												} else if (effectiveStatus === 'Not Achieved') {
													cardClasses = isChecked
														? 'border-rose-500 bg-rose-50 shadow-md'
														: 'border-rose-300 bg-rose-50/60 hover:border-rose-400';
												} else {
													cardClasses = isChecked
														? 'border-indigo-400 bg-indigo-50 shadow-md'
														: 'border-gray-200 bg-white hover:border-indigo-200';
												}

												return (
													<div
														key={grading.id}
														className={`rounded-xl border p-4 shadow-sm transition ${cardClasses}`}
														onDoubleClick={() => toggleSelectedGrading(grading.id)}
														onClick={() => toggleSelectedGrading(grading.id)}
													>
														<div className="flex items-start gap-4">
															<div>
																<input
																	type="checkbox"
																	checked={isChecked}
																	onChange={() => toggleSelectedGrading(grading.id)}
																	onDoubleClick={(event) => event.stopPropagation()}
																	className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
																/>
															</div>
															<div className="flex-1">
																<div className="flex items-start justify-between gap-4">
																	<p className="text-sm font-medium text-gray-900 select-text">{grading.name}</p>
																	<div className="flex items-center gap-2">
																		<button
																			type="button"
																			onClick={async (event) => {
																				event.stopPropagation();
																				const ok = await copyTextToClipboard(grading.name);
																				if (ok) {
																					Swal.fire({
																						icon: 'success',
																						title: 'Copied',
																						text: 'Grading comment copied to clipboard.',
																						toast: true,
																						position: 'top-end',
																						showConfirmButton: false,
																						timer: 2000,
																					});
																				} else {
																					Swal.fire({
																						icon: 'error',
																						title: 'Copy failed',
																						text: 'Could not copy the grading comment.',
																						toast: true,
																						position: 'top-end',
																						showConfirmButton: false,
																						timer: 2000,
																					});
																				}
																			}}
																			className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-100"
																		>
																			Copy
																		</button>
																		<span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClasses}`}>
																			{effectiveStatus ?? 'Status unknown'}
																		</span>
																	</div>
																</div>
																<div className="mt-4 flex flex-wrap items-center gap-4">
																	<label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
																		<input
																			type="radio"
																			name={`grading-status-${grading.id}`}
																			checked={effectiveStatus === 'Achieved'}
																			onChange={() => setGradingStatus(grading.id, 'Achieved')}
																			onDoubleClick={(event) => event.stopPropagation()}
																			className="h-3.5 w-3.5 border-gray-300 text-emerald-600 focus:ring-emerald-500"
																		/>
																		Achieved
																	</label>
																	<label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
																		<input
																			type="radio"
																			name={`grading-status-${grading.id}`}
																			checked={effectiveStatus === 'Not Achieved'}
																			onChange={() => setGradingStatus(grading.id, 'Not Achieved')}
																			onDoubleClick={(event) => event.stopPropagation()}
																			className="h-3.5 w-3.5 border-gray-300 text-rose-600 focus:ring-rose-500"
																		/>
																		Not Achieved
																	</label>
																	<button
																		type="button"
																		onClick={() => setGradingStatus(grading.id, null)}
																		className="text-xs font-medium text-gray-400 underline transition hover:text-gray-600"
																	>
																		Reset
																	</button>
																</div>
															</div>
														</div>
														<p className="mt-3 text-xs text-gray-500 select-text">
															Last updated {updatedDisplay ?? 'recently'}
														</p>
													</div>
												);
											})
										) : (
											<div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
												{selectedCriteria.gradings && selectedCriteria.gradings.length > 0 ? 'No grading templates match your search.' : 'No grading templates defined for this criteria yet.'}
											</div>
										)}
									</div>
								) : (
									<div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
										Select a criteria to view grading details.
									</div>
								)}
							</div>
						</div>
					) : (
						<div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
							Select an assignment to browse its criteria and grading setup.
						</div>
					)}
				</div>
			</div>

			{showSubjectModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div className="w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
						<div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
							<h3 className="text-lg font-semibold text-gray-900">
								{subjectModalMode === 'edit' ? 'Edit Assignment Subject' : 'Manage Assignment Subjects'}
							</h3>
							<button
								type="button"
								onClick={closeSubjectModal}
								className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
								aria-label="Close subject modal"
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<div className="flex-1 space-y-6 px-6 py-5 overflow-y-auto">
							<form onSubmit={submitSubjectForm} className="space-y-3">
								<div>
									<label className="block text-sm font-medium text-gray-700">Subject name</label>
									<input
										type="text"
										value={subjectForm.name}
										onChange={(event) => setSubjectForm({ name: event.target.value })}
										className={`mt-1 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 ${subjectErrors.name ? 'border-red-500 focus:ring-red-500/30' : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500/20'}`}
										placeholder="Enter subject name"
									/>
									{subjectErrors.name && (
										<p className="mt-1 text-xs text-red-600">{subjectErrors.name}</p>
									)}
									{subjectErrors.form && (
										<p className="mt-1 text-xs text-red-600">{subjectErrors.form}</p>
									)}
								</div>
								<div className="flex flex-wrap items-center gap-3">
									<button
										type="submit"
										className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-60"
										disabled={subjectSubmitting}
									>
										{subjectModalMode === 'edit' ? 'Update Subject' : 'Add Subject'}
									</button>
									{subjectModalMode === 'edit' && (
										<button
											type="button"
											onClick={() => {
												setSubjectModalMode('create');
												setEditingSubjectId(null);
												setSubjectForm({ name: '' });
												setSubjectErrors({});
											}}
											className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
										>
											Cancel edit
										</button>
									)}
								</div>
							</form>

							<div className="space-y-3 rounded-xl border border-gray-200 p-4">
								<div className="relative">
									<input
										type="text"
										value={subjectModalSearch}
										onChange={(event) => setSubjectModalSearch(event.target.value)}
										className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
										placeholder="Search subjects"
									/>
									<svg
										className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
									</svg>
								</div>
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Assignments</th>
												<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-200 bg-white">
											{filteredSubjectsForModal.length > 0 ? (
												filteredSubjectsForModal.map((subject, index) => (
													<tr key={subject.id} className="hover:bg-gray-50">
														<td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
														<td className="px-4 py-3 text-sm font-medium text-gray-900">{subject.name}</td>
														<td className="px-4 py-3 text-sm text-gray-500">{subject.assignment_count}</td>
														<td className="px-4 py-3 text-sm">
															<div className="flex items-center justify-end gap-2">
																<button
																	type="button"
																	onClick={() => editSubject(subject)}
																	className="rounded-lg bg-blue-100 px-3 py-1.5 text-blue-700 transition hover:bg-blue-200"
																>
																	Edit
																</button>
																<button
																	type="button"
																	onClick={() => deleteSubject(subject)}
																	className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 transition hover:bg-red-200"
																>
																	Delete
																</button>
															</div>
														</td>
													</tr>
												))
											) : (
												<tr>
													<td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={4}>
														{subjectModalSearch.trim() ? 'No subjects match your search.' : 'No assignment subjects available.'}
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{showAssignmentModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div className="w-full max-w-3xl max-h-[80vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
						<div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
							<h3 className="text-lg font-semibold text-gray-900">
								{assignmentModalMode === 'edit' ? 'Edit Assignment' : 'Manage Assignments'}
							</h3>
							<button
								type="button"
								onClick={closeAssignmentModal}
								className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
								aria-label="Close assignment modal"
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<div className="flex-1 space-y-6 px-6 py-5 overflow-y-auto">
							{assignmentModalMode === 'create' && (
								<div className="rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-xs text-indigo-700">
									Subject automatically follows the “Assignment Subject” selection above. Change it there before adding a new assignment.
								</div>
							)}
							<form onSubmit={submitAssignmentForm} className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div>
										<label className="block text-sm font-medium text-gray-700">Subject</label>
										<Select
											value={assignmentForm.subject_id ? subjectOptions.find((option) => option.value === assignmentForm.subject_id) ?? null : null}
											onChange={(option) => {
												setAssignmentErrors((prev) => {
													const { subject_id, ...rest } = prev;
													return rest;
												});
												setAssignmentForm((prev) => ({
													...prev,
													subject_id: option ? option.value : null,
												}));
											}}
											options={subjectOptions}
											placeholder={assignmentModalMode === 'create' ? 'Subject follows the main selection' : 'Select subject'}
											classNamePrefix="react-select"
											styles={selectStyles}
											menuPortalTarget={menuPortalTarget}
											isDisabled={assignmentModalMode === 'create'}
										/>
										{assignmentModalMode === 'create' && (
											<p className="mt-1 text-xs text-gray-500">
												Need a different subject? Close this modal and pick another option from “Assignment Subject”.
											</p>
										)}
										{assignmentErrors.subject_id && (
											<p className="mt-1 text-xs text-red-600">{assignmentErrors.subject_id}</p>
										)}
										{assignmentErrors.form && (
											<p className="mt-1 text-xs text-red-600">{assignmentErrors.form}</p>
										)}
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700">Assignment title</label>
										<input
											type="text"
											value={assignmentForm.title}
											onChange={(event) => setAssignmentForm((prev) => ({ ...prev, title: event.target.value }))}
											className={`mt-1 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 ${assignmentErrors.title ? 'border-red-500 focus:ring-red-500/30' : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500/20'}`}
											placeholder="Enter assignment title"
										/>
										{assignmentErrors.title && (
											<p className="mt-1 text-xs text-red-600">{assignmentErrors.title}</p>
										)}
									</div>
								</div>
								<div className="flex flex-wrap items-center gap-3">
									<button
										type="submit"
										className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
										disabled={assignmentSubmitting}
									>
										{assignmentModalMode === 'edit' ? 'Update Assignment' : 'Add Assignment'}
									</button>
									{assignmentModalMode === 'edit' && (
										<button
											type="button"
											onClick={() => {
												setAssignmentModalMode('create');
												setEditingAssignmentId(null);
												setAssignmentForm({
													title: '',
													subject_id: selectedSubject?.id ?? assignmentSubjects[0]?.id ?? null,
												});
												setAssignmentErrors({});
											}}
											className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
										>
											Cancel edit
										</button>
									)}
								</div>
							</form>

							<div className="space-y-3 rounded-xl border border-gray-200 p-4">
								<div className="relative">
									<input
										type="text"
										value={assignmentModalSearch}
										onChange={(event) => setAssignmentModalSearch(event.target.value)}
										className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
										placeholder="Search assignments"
									/>
									<svg
										className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
									</svg>
								</div>
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Assignment</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
												<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-200 bg-white">
											{filteredAssignmentsForModal.length > 0 ? (
												filteredAssignmentsForModal.map(({ assignment, subject }, index) => (
													<tr key={assignment.id} className="hover:bg-gray-50">
														<td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
														<td className="px-4 py-3 text-sm font-medium text-gray-900">{assignment.title}</td>
														<td className="px-4 py-3 text-sm text-gray-500">{subject.name}</td>
														<td className="px-4 py-3 text-sm">
															<div className="flex items-center justify-end gap-2">
																<button
																	type="button"
																	onClick={() => editAssignment(assignment)}
																	className="rounded-lg bg-blue-100 px-3 py-1.5 text-blue-700 transition hover:bg-blue-200"
																>
																	Edit
																</button>
																<button
																	type="button"
																	onClick={() => deleteAssignment(assignment)}
																	className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 transition hover:bg-red-200"
																>
																	Delete
																</button>
															</div>
														</td>
													</tr>
												))
											) : (
												<tr>
													<td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={4}>
														{assignmentForm.subject_id
															? assignmentModalSearch.trim()
																? 'No assignments match your search.'
																: 'No assignments found for the selected subject.'
															: 'Select a subject to view assignments.'}
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{showCriteriaModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
						<div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
							<h3 className="text-lg font-semibold text-gray-900">
								{criteriaModalMode === 'edit' ? 'Edit Criteria' : 'Manage Criteria'}
							</h3>
							<button
								type="button"
								onClick={closeCriteriaModal}
								className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
								aria-label="Close criteria modal"
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
							{assignmentOptionsForCriteriaModal.length === 0 && (
								<div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-xs text-yellow-700">
									Add an assignment first to start defining criteria.
								</div>
							)}
							<form onSubmit={submitCriteriaForm} className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div>
										<label className="block text-sm font-medium text-gray-700">Assignment</label>
										<Select
											value={criteriaForm.assignment_id ? assignmentOptionsForCriteriaModal.find((option) => option.value === criteriaForm.assignment_id) ?? null : null}
											onChange={(option) => {
												setCriteriaErrors((prev) => {
													const { assignment_id, ...rest } = prev;
													return rest;
												});
												setCriteriaForm((prev) => ({
													...prev,
													assignment_id: option ? option.value : null,
												}));
											}}
											options={assignmentOptionsForCriteriaModal}
											placeholder="Select assignment"
											classNamePrefix="react-select"
											styles={selectStyles}
											menuPortalTarget={menuPortalTarget}
											isClearable
										/>
										{criteriaErrors.assignment_id && (
											<p className="mt-1 text-xs text-red-600">{criteriaErrors.assignment_id}</p>
										)}
										{criteriaErrors.form && (
											<p className="mt-1 text-xs text-red-600">{criteriaErrors.form}</p>
										)}
									</div>
									<div className="md:col-span-2">
										<label className="block text-sm font-medium text-gray-700">Criteria name</label>
										<textarea
											value={criteriaForm.name}
											onChange={(event) => setCriteriaForm((prev) => ({ ...prev, name: event.target.value }))}
											rows={3}
											className={`mt-1 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 ${criteriaErrors.name ? 'border-red-500 focus:ring-red-500/30' : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500/20'}`}
											placeholder="Describe the criteria"
										/>
										{criteriaErrors.name && (
											<p className="mt-1 text-xs text-red-600">{criteriaErrors.name}</p>
										)}
									</div>
								</div>
								<div className="flex flex-wrap items-center gap-3">
									<button
										type="submit"
										className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-60"
										disabled={criteriaSubmitting}
									>
										{criteriaModalMode === 'edit' ? 'Update Criteria' : 'Add Criteria'}
									</button>
									{criteriaModalMode === 'edit' && (
										<button
											type="button"
											onClick={() => {
												setCriteriaModalMode('create');
												setEditingCriteriaId(null);
												setCriteriaForm({ assignment_id: criteriaForm.assignment_id, name: '' });
												setCriteriaErrors({});
											}}
											className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
										>
											Cancel edit
										</button>
									)}
								</div>
							</form>

							<div className="space-y-3 rounded-xl border border-gray-200 p-4">
								<div className="relative">
									<input
										type="text"
										value={criteriaModalSearch}
										onChange={(event) => setCriteriaModalSearch(event.target.value)}
										className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
										placeholder="Search criteria"
									/>
									<svg
										className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
									</svg>
								</div>
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Criteria</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Assignment</th>
												<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-200 bg-white">
											{filteredCriteriaRowsForModal.length > 0 ? (
												filteredCriteriaRowsForModal.map(({ criteria, assignment, subject }, index) => (
													<tr key={criteria.id} className="hover:bg-gray-50">
														<td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
														<td className="px-4 py-3 text-sm font-medium text-gray-900">{criteria.name}</td>
														<td className="px-4 py-3 text-sm text-gray-500">{subject.name} · {assignment.title}</td>
														<td className="px-4 py-3 text-sm">
															<div className="flex items-center justify-end gap-2">
																<button
																	type="button"
																	onClick={() => editCriteria(criteria.id)}
																	className="rounded-lg bg-blue-100 px-3 py-1.5 text-blue-700 transition hover:bg-blue-200"
																>
																	Edit
																</button>
																<button
																	type="button"
																	onClick={() => deleteCriteria(criteria.id)}
																	className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 transition hover:bg-red-200"
																>
																	Delete
																</button>
															</div>
														</td>
													</tr>
												))
											) : (
												<tr>
													<td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={4}>
														{criteriaForm.assignment_id
															? criteriaModalSearch.trim()
																? 'No criteria match your search.'
																: 'No criteria defined for the selected assignment yet.'
															: 'Select an assignment to see existing criteria.'}
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{showGradingModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div className="flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
						<div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
							<h3 className="text-lg font-semibold text-gray-900">
								{gradingModalMode === 'edit' ? 'Edit Grading Template' : 'Manage Grading Templates'}
							</h3>
							<button
								type="button"
								onClick={closeGradingModal}
								className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
								aria-label="Close grading modal"
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
							{criteriaOptionsForGradingModal.length === 0 && (
								<div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-xs text-yellow-700">
									Create criteria first to add grading templates.
								</div>
							)}
							<form onSubmit={submitGradingForm} className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div>
										<label className="block text-sm font-medium text-gray-700">Criteria</label>
										<Select
											value={gradingForm.criteria_id ? criteriaOptionsForGradingModal.find((option) => option.value === gradingForm.criteria_id) ?? null : null}
											onChange={(option) => {
												setGradingErrors((prev) => {
													const { criteria_id, ...rest } = prev;
													return rest;
												});
												setGradingForm((prev) => ({
													...prev,
													criteria_id: option ? option.value : null,
												}));
											}}
											options={criteriaOptionsForGradingModal}
											placeholder="Select criteria"
											classNamePrefix="react-select"
											styles={selectStyles}
											menuPortalTarget={menuPortalTarget}
											isClearable
										/>
										{gradingErrors.criteria_id && (
											<p className="mt-1 text-xs text-red-600">{gradingErrors.criteria_id}</p>
										)}
										{gradingErrors.form && (
											<p className="mt-1 text-xs text-red-600">{gradingErrors.form}</p>
										)}
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700">Default status</label>
										<select
											value={gradingForm.status ?? ''}
											onChange={(event) => {
												const nextValue = event.target.value === '' ? null : event.target.value;
												setGradingForm((prev) => ({ ...prev, status: nextValue }));
											}}
											className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
										>
											<option value="">No default</option>
											<option value="Achieved">Achieved</option>
											<option value="Not Achieved">Not Achieved</option>
										</select>
									</div>
									<div className="md:col-span-2">
										<label className="block text-sm font-medium text-gray-700">Grading comment</label>
										<textarea
											value={gradingForm.name}
											onChange={(event) => setGradingForm((prev) => ({ ...prev, name: event.target.value }))}
											rows={4}
											className={`mt-1 w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 ${gradingErrors.name ? 'border-red-500 focus:ring-red-500/30' : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500/20'}`}
											placeholder="Provide the feedback template"
										/>
										{gradingErrors.name && (
											<p className="mt-1 text-xs text-red-600">{gradingErrors.name}</p>
										)}
									</div>
								</div>
								<div className="flex flex-wrap items-center gap-3">
									<button
										type="submit"
										className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-60"
										disabled={gradingSubmitting}
									>
										{gradingModalMode === 'edit' ? 'Update Grading' : 'Add Grading'}
									</button>
									{gradingModalMode === 'edit' && (
										<button
											type="button"
											onClick={() => {
												setGradingModalMode('create');
												setEditingGradingId(null);
												setGradingForm({ criteria_id: gradingForm.criteria_id, name: '', status: gradingForm.status });
												setGradingErrors({});
											}}
											className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
										>
											Cancel edit
										</button>
									)}
								</div>
							</form>

							<div className="space-y-3 rounded-xl border border-gray-200 p-4">
								<div className="relative">
									<input
										type="text"
										value={gradingModalSearch}
										onChange={(event) => setGradingModalSearch(event.target.value)}
										className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
										placeholder="Search grading templates"
									/>
									<svg
										className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
									</svg>
								</div>
								<div className="overflow-x-auto">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Comment</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
												<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Criteria</th>
												<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-200 bg-white">
											{filteredGradingRowsForModal.length > 0 ? (
												filteredGradingRowsForModal.map(({ grading, criteria, assignment, subject }, index) => {
													const badgeClasses = grading.status === 'Achieved'
														? 'bg-emerald-100 text-emerald-700'
														: grading.status === 'Not Achieved'
															? 'bg-rose-100 text-rose-700'
															: 'bg-gray-100 text-gray-600';
													return (
														<tr key={grading.id} className="hover:bg-gray-50">
															<td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
															<td className="px-4 py-3 text-sm text-gray-900">{grading.name}</td>
															<td className="px-4 py-3 text-sm">
																<span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClasses}`}>
																	{grading.status ?? 'No default'}
																</span>
															</td>
															<td className="px-4 py-3 text-sm text-gray-500">{subject.name} · {assignment.title} · {criteria.name}</td>
															<td className="px-4 py-3 text-sm">
																<div className="flex items-center justify-end gap-2">
																	<button
																		type="button"
																		onClick={() => editGrading(grading.id)}
																		className="rounded-lg bg-blue-100 px-3 py-1.5 text-blue-700 transition hover:bg-blue-200"
																	>
																		Edit
																	</button>
																	<button
																		type="button"
																		onClick={() => deleteGrading(grading.id)}
																		className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 transition hover:bg-red-200"
																	>
																		Delete
																	</button>
																</div>
															</td>
														</tr>
													);
												})
											) : (
												<tr>
													<td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={5}>
														{gradingForm.criteria_id || selectedCriteria
															? gradingModalSearch.trim()
																? 'No grading templates match your search.'
																: 'No grading templates for the selected criteria yet.'
															: gradingModalSearch.trim()
																? 'No grading templates match your search.'
																: 'No grading templates available. Add one to get started.'}
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{showPromptPreviewModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
					<div className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
						<div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
							<h3 className="text-lg font-semibold text-gray-900">Preview &amp; Gemini comment</h3>
							<button
								type="button"
								onClick={closePromptPreviewModal}
								className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
								aria-label="Close preview modal"
							>
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
							<div className="flex flex-col gap-3 rounded-xl bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
								<div>
									<p className="text-sm font-medium text-gray-700">Target feedback length</p>
									<p className="text-xs text-gray-500">Gemini will aim for approximately this many words in the comment.</p>
								</div>
								<input
									type="number"
									value={feedbackWordCount}
									onChange={(event) => {
										const raw = event.target.value;
										const numeric = Number(raw);
										if (!Number.isNaN(numeric) && numeric > 0) {
											setFeedbackWordCount(numeric);
											const updated = buildChatGptPrompt(numeric);
											if (updated) {
												setPromptPreviewText(updated);
											}
										}
									}}
									className="w-24 rounded-lg border border-gray-300 px-3 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
								/>
							</div>

							<div className="space-y-2">
								<p className="text-sm font-medium text-gray-700">Prompt to send to AI</p>
								<textarea
									value={promptPreviewText}
									onChange={(event) => setPromptPreviewText(event.target.value)}
									rows={6}
									className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
								/>
							</div>

							<div className="space-y-3 border-t border-gray-200 pt-4">
								<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
									<p className="text-sm font-medium text-gray-700">Gemini-generated comment</p>
									<button
										type="button"
										onClick={handleGenerateGeminiComment}
										className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
										disabled={geminiLoading}
									>
										{geminiLoading ? 'Generating…' : 'Generate Gemini comment'}
									</button>
								</div>
								{geminiError && <p className="text-xs text-red-600">{geminiError}</p>}
								<textarea
									value={geminiResponseText}
									onChange={(event) => setGeminiResponseText(event.target.value)}
									rows={6}
									className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
									placeholder="Gemini response will appear here..."
								/>
								<div className="flex justify-end">
									<button
										type="button"
										onClick={copyGeminiResponseText}
										className="inline-flex items-center justify-center rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
									>
										Copy Gemini response
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</DashboardLayout>
	);
}
